import { Directive, DoCheck, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

/**
 * Stops one save from becoming two records.
 *
 * <p>Most modal footers in this app have nothing stopping a second click, and a save that takes a
 * moment invites one: the button gives no sign that the first click registered. Linking an employee
 * to a project produced duplicate rows exactly this way. `double-click-guard.spec.ts` keeps the
 * footers that commit something from drifting back.
 *
 * <p>On the first click the button disables itself, so the browser drops any further clicks, and
 * it shows the app's loading state so the user can see that something is happening. There is no
 * event interception involved, which means the click that got through still reaches every handler
 * that was listening for it.
 *
 * <p>It re-asserts that on every check, because most of these buttons also carry a `[disabled]`
 * binding of their own -- typically `!form.valid || loading` -- and Angular would otherwise write
 * `false` back over the guard on the next change detection pass.
 *
 * <p>The guard cannot know whether the click actually started a save. A handler that validates
 * first -- "Please select an employee", and the dialog stays open -- saves nothing, and holding
 * the button after one of those locks the user out of the very action they are trying to perform.
 * So the guard also lifts the moment the user touches anything else: picking from a dropdown,
 * typing in a field, clicking elsewhere in the dialog. Someone doing that is no longer
 * double-clicking, and the next click is a fresh attempt rather than a repeat of the last one.
 *
 * <p>Failing that it lifts after `guardWindowMs`. On the common path neither matters: a successful
 * save closes the modal and takes the button with it. A save slower than the window can still be
 * clicked twice; the real fix for those is a loading flag in the component, and this does not
 * replace one.
 */
@Directive({
    selector: '[appGuardDoubleClick]',
    standalone: false,
})
export class GuardDoubleClickDirective implements DoCheck, OnDestroy {
    /** Long enough to cover a slow save, short enough that a failed one can be retried. */
    @Input() guardWindowMs = 6000;

    private busy = false;
    private releaseTimer: ReturnType<typeof setTimeout>;

    constructor(private readonly elementRef: ElementRef<HTMLButtonElement>, private readonly zone: NgZone) {
        this.elementRef.nativeElement.addEventListener('click', () => this.hold());
    }

    ngDoCheck() {
        if (this.busy) {
            this.apply();
        }
    }

    ngOnDestroy() {
        this.stopWaiting();
    }

    private hold() {
        const button = this.elementRef.nativeElement;
        if (this.busy || button.disabled) {
            return;
        }
        this.busy = true;
        this.apply();
        // Outside Angular: these are a safety net, not something the view depends on, and running
        // them inside the zone would keep change detection awake for no reason.
        this.zone.runOutsideAngular(() => {
            document.addEventListener('pointerdown', this.releaseOnOtherInput, true);
            document.addEventListener('keydown', this.releaseOnOtherInput, true);
            this.releaseTimer = setTimeout(() => this.zone.run(() => this.release()), this.guardWindowMs);
        });
    }

    /** Anything the user does other than press this button again means they have moved on. */
    private readonly releaseOnOtherInput = (event: Event) => {
        const button = this.elementRef.nativeElement;
        const target = event.target as Node | null;
        if (target && (target === button || button.contains(target))) {
            return;
        }
        this.zone.run(() => this.release());
    };

    private apply() {
        const button = this.elementRef.nativeElement;
        button.disabled = true;
        button.classList.add('button--loading');
    }

    private release() {
        this.stopWaiting();
        this.busy = false;
        const button = this.elementRef.nativeElement;
        button.disabled = false;
        button.classList.remove('button--loading');
    }

    private stopWaiting() {
        clearTimeout(this.releaseTimer);
        document.removeEventListener('pointerdown', this.releaseOnOtherInput, true);
        document.removeEventListener('keydown', this.releaseOnOtherInput, true);
    }
}
